import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import api from '../../services/api';
import {
  OrderCategory,
  OrderSubcategory,
  SubcategoryProductFieldItem,
  ProductFieldOptionPayload,
  UpstreamCarrier,
} from '../../types/order';

const NBN_TECH_OPTIONS: ProductFieldOptionPayload[] = [
  { value: 'FTTP', label: 'FTTP' },
  { value: 'FTTB', label: 'FTTB' },
  { value: 'FTTC', label: 'FTTC' },
  { value: 'FTTN', label: 'FTTN' },
  { value: 'HFC', label: 'HFC' },
  { value: 'FW', label: 'FW' },
];

const CUSTOMER_TYPE_OPTIONS: ProductFieldOptionPayload[] = [
  { value: 'residential', label: 'Residential' },
  { value: 'business', label: 'Business' },
];

/** Suggested internet plan field keys (used by "Load template" in Product fields dialog). */
const INTERNET_PLAN_FIELD_TEMPLATE: {
  key: string;
  label: string;
  field_type?: string;
  field_options?: ProductFieldOptionPayload[];
}[] = [
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
  { key: 'speed_tier', label: 'Speed tier' },
  { key: 'data_cap', label: 'Data cap' },
  { key: 'typical_evening_speed', label: 'Typical evening speed (7pm–11pm)' },
  { key: 'features', label: 'Features', field_type: 'multiline_text' },
  { key: 'monthly_price', label: 'Monthly price' },
  { key: 'setup_price', label: 'Setup price' },
  { key: 'one_time_price', label: 'One-time price' },
  { key: 'contract_terms', label: 'Contract terms' },
  { key: 'max_channels', label: 'Max channels' },
  {
    key: 'available_technology_types',
    label: 'Technology types (plan available for)',
    field_type: 'checkboxes',
    field_options: NBN_TECH_OPTIONS,
  },
  {
    key: 'customer_type',
    label: 'Residential / Business',
    field_type: 'dropdown',
    field_options: CUSTOMER_TYPE_OPTIONS,
  },
];

const PRODUCT_FIELD_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'single_line_text', label: 'Single line text' },
  { value: 'multiline_text', label: 'Multiline text' },
  { value: 'rich_text', label: 'Multiline / rich text' },
  { value: 'checkbox', label: 'Single checkbox' },
  { value: 'checkboxes', label: 'Multiple checkboxes' },
  { value: 'dropdown', label: 'Dropdown' },
];

function productFieldTypesRequiringOptions(fieldType: string): boolean {
  return fieldType === 'checkboxes' || fieldType === 'dropdown';
}

type ProductFieldOptionRow = ProductFieldOptionPayload & { _oid: string };

type ProductFieldRow = Omit<SubcategoryProductFieldItem, 'field_options'> & {
  _rowId: string;
  field_options: ProductFieldOptionRow[];
};

const newProductFieldRowId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `pf-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

function mapApiProductFieldToRow(f: Record<string, unknown>): ProductFieldRow {
  const rawOpts = f.field_options;
  const opts: ProductFieldOptionRow[] = [];
  if (Array.isArray(rawOpts)) {
    for (const it of rawOpts) {
      if (it && typeof it === 'object' && !Array.isArray(it)) {
        const o = it as Record<string, unknown>;
        const value = String(o.value ?? '').trim();
        const label = String(o.label ?? o.value ?? '').trim() || value;
        opts.push({ _oid: newProductFieldRowId(), value, label });
      }
    }
  }
  const id = f.id;
  const rawType = String(f.field_type ?? 'single_line_text').toLowerCase() || 'single_line_text';
  const field_type = PRODUCT_FIELD_TYPE_OPTIONS.some((o) => o.value === rawType) ? rawType : 'single_line_text';
  return {
    _rowId: id != null && String(id) !== '' ? String(id) : newProductFieldRowId(),
    field_key: String(f.field_key ?? ''),
    display_order: Number(f.display_order ?? 0),
    default_display_label: f.default_display_label != null ? String(f.default_display_label) : '',
    label_override: f.label_override != null ? String(f.label_override) : undefined,
    is_visible: Boolean(f.is_visible ?? true),
    field_type,
    field_options: opts,
  };
}

/** Hardware fields that can be shown on equipment cards per category. */
const HARDWARE_FIELD_KEYS: { key: string; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
  { key: 'hardware_type', label: 'Type (Router/ATA/Other)' },
  { key: 'price', label: 'Price' },
  { key: 'delivery_fee', label: 'Delivery fee' },
  { key: 'discounted_price', label: 'Discounted price' },
  { key: 'image', label: 'Image' },
];

function CategoriesPage() {
  const [categories, setCategories] = useState<OrderCategory[]>([]);
  const [subcategories, setSubcategories] = useState<OrderSubcategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  /** Nested tabs when main tab is Product fields: category defaults vs subcategory overrides. */
  const [productFieldsMainSubTab, setProductFieldsMainSubTab] = useState(0);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [subcategoryDialogOpen, setSubcategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<OrderCategory | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<OrderSubcategory | null>(null);
  const [productFieldsDialogOpen, setProductFieldsDialogOpen] = useState(false);
  const [editingProductFieldsSubcategory, setEditingProductFieldsSubcategory] = useState<OrderSubcategory | null>(null);
  const [editingProductFieldsCategory, setEditingProductFieldsCategory] = useState<OrderCategory | null>(null);
  const [productFieldsList, setProductFieldsList] = useState<ProductFieldRow[]>([]);
  const [hardwareFieldsDialogOpen, setHardwareFieldsDialogOpen] = useState(false);
  const [editingHardwareFieldsCategory, setEditingHardwareFieldsCategory] = useState<OrderCategory | null>(null);
  const [hardwareFieldsList, setHardwareFieldsList] = useState<SubcategoryProductFieldItem[]>([]);
  const [upstreamCarriers, setUpstreamCarriers] = useState<UpstreamCarrier[]>([]);
  const [upstreamCarrierDialogOpen, setUpstreamCarrierDialogOpen] = useState(false);
  const [editingUpstreamCarrier, setEditingUpstreamCarrier] = useState<UpstreamCarrier | null>(null);
  const [upstreamCarrierFormData, setUpstreamCarrierFormData] = useState({ name: '', display_order: 0, is_active: true });
  
  // Category form state
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    slug: '',
    description: '',
    logo_url: '',
    display_order: 0,
    is_active: true,
  });
  
  // Subcategory form state
  const [subcategoryFormData, setSubcategoryFormData] = useState({
    name: '',
    slug: '',
    description: '',
    logo_url: '',
    display_order: 0,
    is_active: true,
    category_id: '',
  });
  
  useEffect(() => {
    loadData();
  }, []);

  const sortedProductFields = useMemo(
    () =>
      [...productFieldsList].sort(
        (a, b) => a.display_order - b.display_order || a.field_key.localeCompare(b.field_key),
      ),
    [productFieldsList],
  );

  const updateProductFieldRow = useCallback((rowId: string, patch: Partial<Omit<ProductFieldRow, '_rowId'>>) => {
    setProductFieldsList((prev) => prev.map((f) => (f._rowId === rowId ? { ...f, ...patch } : f)));
  }, []);

  const addProductFieldOption = useCallback((rowId: string) => {
    setProductFieldsList((prev) =>
      prev.map((f) =>
        f._rowId === rowId
          ? { ...f, field_options: [...f.field_options, { _oid: newProductFieldRowId(), value: '', label: '' }] }
          : f,
      ),
    );
  }, []);

  const updateProductFieldOptionRow = useCallback(
    (rowId: string, optionId: string, patch: Partial<ProductFieldOptionPayload>) => {
      setProductFieldsList((prev) =>
        prev.map((f) =>
          f._rowId !== rowId
            ? f
            : {
                ...f,
                field_options: f.field_options.map((o) => (o._oid === optionId ? { ...o, ...patch } : o)),
              },
        ),
      );
    },
    [],
  );

  const removeProductFieldOptionRow = useCallback((rowId: string, optionId: string) => {
    setProductFieldsList((prev) =>
      prev.map((f) =>
        f._rowId === rowId ? { ...f, field_options: f.field_options.filter((o) => o._oid !== optionId) } : f,
      ),
    );
  }, []);

  const removeProductFieldRow = useCallback((rowId: string) => {
    setProductFieldsList((prev) => prev.filter((f) => f._rowId !== rowId));
  }, []);

  const moveProductFieldRow = useCallback((index: number, delta: number) => {
    setProductFieldsList((prev) => {
      const s = [...prev].sort((a, b) => a.display_order - b.display_order);
      const next = index + delta;
      if (next < 0 || next >= s.length) return prev;
      const itemA = s[index];
      const itemB = s[next];
      return prev.map((f) => {
        if (f._rowId === itemA._rowId) return { ...f, display_order: itemB.display_order };
        if (f._rowId === itemB._rowId) return { ...f, display_order: itemA.display_order };
        return f;
      });
    });
  }, []);
  
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [categoriesData, subcategoriesData, upstreamCarriersData] = await Promise.all([
        api.adminListCategories(),
        api.adminListSubcategories(),
        api.adminListUpstreamCarriers(false),
      ]);
      
      setCategories(categoriesData);
      setSubcategories(subcategoriesData);
      setUpstreamCarriers(upstreamCarriersData);
      
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenCategoryDialog = (category?: OrderCategory) => {
    if (category) {
      setEditingCategory(category);
      setCategoryFormData({
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        logo_url: category.logo_url || '',
        display_order: category.display_order,
        is_active: category.is_active,
      });
    } else {
      setEditingCategory(null);
      setCategoryFormData({
        name: '',
        slug: '',
        description: '',
        logo_url: '',
        display_order: 0,
        is_active: true,
      });
    }
    setCategoryDialogOpen(true);
  };
  
  const handleOpenSubcategoryDialog = (subcategory?: OrderSubcategory) => {
    if (subcategory) {
      setEditingSubcategory(subcategory);
      setSubcategoryFormData({
        name: subcategory.name,
        slug: subcategory.slug,
        description: subcategory.description || '',
        logo_url: subcategory.logo_url || '',
        display_order: subcategory.display_order,
        is_active: subcategory.is_active,
        category_id: subcategory.category_id,
      });
    } else {
      setEditingSubcategory(null);
      setSubcategoryFormData({
        name: '',
        slug: '',
        description: '',
        logo_url: '',
        display_order: 0,
        is_active: true,
        category_id: '',
      });
    }
    setSubcategoryDialogOpen(true);
  };
  
  const handleSaveCategory = async () => {
    try {
      setError(null);
      
      const categoryData: any = {
        name: categoryFormData.name,
        slug: categoryFormData.slug,
        description: categoryFormData.description || undefined,
        logo_url: categoryFormData.logo_url || undefined,
        display_order: categoryFormData.display_order,
        is_active: categoryFormData.is_active,
      };
      
      if (editingCategory) {
        await api.adminUpdateCategory(editingCategory.id, categoryData);
      } else {
        await api.adminCreateCategory(categoryData);
      }
      
      setCategoryDialogOpen(false);
      loadData();
      
    } catch (err: any) {
      setError(err.message || 'Failed to save category');
    }
  };
  
  const handleSaveSubcategory = async () => {
    try {
      setError(null);
      
      const subcategoryData: any = {
        name: subcategoryFormData.name,
        slug: subcategoryFormData.slug,
        description: subcategoryFormData.description || undefined,
        logo_url: subcategoryFormData.logo_url || undefined,
        display_order: subcategoryFormData.display_order,
        is_active: subcategoryFormData.is_active,
        category_id: subcategoryFormData.category_id,
      };
      
      if (editingSubcategory) {
        await api.adminUpdateSubcategory(editingSubcategory.id, subcategoryData);
      } else {
        await api.adminCreateSubcategory(subcategoryData);
      }
      
      setSubcategoryDialogOpen(false);
      loadData();
      
    } catch (err: any) {
      setError(err.message || 'Failed to save subcategory');
    }
  };

  const handleOpenUpstreamCarrierDialog = (carrier?: UpstreamCarrier) => {
    if (carrier) {
      setEditingUpstreamCarrier(carrier);
      setUpstreamCarrierFormData({
        name: carrier.name,
        display_order: carrier.display_order,
        is_active: carrier.is_active,
      });
    } else {
      setEditingUpstreamCarrier(null);
      setUpstreamCarrierFormData({ name: '', display_order: upstreamCarriers.length, is_active: true });
    }
    setUpstreamCarrierDialogOpen(true);
  };

  const handleSaveUpstreamCarrier = async () => {
    try {
      setError(null);
      if (editingUpstreamCarrier) {
        await api.adminUpdateUpstreamCarrier(editingUpstreamCarrier.id, {
          name: upstreamCarrierFormData.name,
          display_order: upstreamCarrierFormData.display_order,
          is_active: upstreamCarrierFormData.is_active,
        });
      } else {
        await api.adminCreateUpstreamCarrier(upstreamCarrierFormData);
      }
      setUpstreamCarrierDialogOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save upstream carrier');
    }
  };

  const handleDeleteUpstreamCarrier = async (carrier: UpstreamCarrier) => {
    if (!window.confirm(`Delete upstream carrier "${carrier.name}"?`)) return;
    try {
      setError(null);
      await api.adminDeleteUpstreamCarrier(carrier.id);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to delete upstream carrier');
    }
  };
  
  const handleDeleteCategory = async (categoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this category? This will also delete all subcategories.')) {
      return;
    }
    
    try {
      await api.adminDeleteCategory(categoryId);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete category');
    }
  };
  
  const handleDeleteSubcategory = async (subcategoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this subcategory?')) {
      return;
    }
    
    try {
      await api.adminDeleteSubcategory(subcategoryId);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete subcategory');
    }
  };

  const handleOpenProductFieldsDialog = async (subcategory: OrderSubcategory) => {
    setEditingProductFieldsCategory(null);
    setEditingProductFieldsSubcategory(subcategory);
    setProductFieldsDialogOpen(true);
    try {
      const saved = await api.adminGetSubcategoryProductFields(subcategory.id);
      const sorted = [...(saved || [])].sort((a, b) => a.display_order - b.display_order);
      setProductFieldsList(sorted.map((f) => mapApiProductFieldToRow(f as unknown as Record<string, unknown>)));
    } catch (err: any) {
      setError(err.message || 'Failed to load product fields');
      setProductFieldsList([]);
    }
  };

  const handleOpenCategoryProductFieldsDialog = async (category: OrderCategory) => {
    setEditingProductFieldsSubcategory(null);
    setEditingProductFieldsCategory(category);
    setProductFieldsDialogOpen(true);
    try {
      const saved = await api.adminGetCategoryProductFields(category.id);
      const sorted = [...(saved || [])].sort((a, b) => a.display_order - b.display_order);
      setProductFieldsList(sorted.map((f) => mapApiProductFieldToRow(f as unknown as Record<string, unknown>)));
    } catch (err: any) {
      setError(err.message || 'Failed to load category product fields');
      setProductFieldsList([]);
    }
  };

  const addProductFieldRow = () => {
    setProductFieldsList((prev) => {
      const nextOrd = prev.length ? Math.max(...prev.map((x) => x.display_order), 0) + 1 : 0;
      return [
        ...prev,
        {
          _rowId: newProductFieldRowId(),
          field_key: '',
          display_order: nextOrd,
          default_display_label: '',
          label_override: undefined,
          is_visible: true,
          field_type: 'single_line_text',
          field_options: [],
        },
      ];
    });
  };

  const loadInternetPlanFieldTemplate = () => {
    setProductFieldsList((prev) => {
      const existing = new Set(prev.map((p) => p.field_key).filter(Boolean));
      const additions = INTERNET_PLAN_FIELD_TEMPLATE.filter((t) => !existing.has(t.key)).map((t, i) => ({
        _rowId: newProductFieldRowId(),
        field_key: t.key,
        display_order: prev.length + i,
        default_display_label: t.label,
        label_override: undefined,
        is_visible: ['name', 'description', 'monthly_price', 'setup_price', 'contract_terms'].includes(t.key),
        field_type: t.field_type || 'single_line_text',
        field_options: (t.field_options || []).map((o) => ({
          _oid: newProductFieldRowId(),
          value: o.value,
          label: o.label,
        })),
      }));
      const merged = [...prev, ...additions];
      return merged.map((f, i) => ({ ...f, display_order: i }));
    });
  };

  const handleSaveProductFields = async () => {
    if (!editingProductFieldsSubcategory && !editingProductFieldsCategory) return;
    try {
      setError(null);
      const sorted = [...productFieldsList].sort((a, b) => a.display_order - b.display_order);
      const trimmed = sorted
        .map((f) => ({ ...f, field_key: f.field_key.trim() }))
        .filter((f) => f.field_key.length > 0);
      const keys = trimmed.map((f) => f.field_key);
      if (new Set(keys).size !== keys.length) {
        setError('Duplicate field keys are not allowed');
        return;
      }
      const keyRe = /^[a-z][a-z0-9_]*$/;
      for (const f of trimmed) {
        if (!keyRe.test(f.field_key)) {
          setError(
            `Invalid field key "${f.field_key}". Use lowercase letters, numbers, and underscores; must start with a letter.`,
          );
          return;
        }
      }
      for (const f of trimmed) {
        const ft = (f.field_type || 'single_line_text').trim();
        if (!productFieldTypesRequiringOptions(ft)) continue;
        const opts = f.field_options
          .map((o) => ({
            value: o.value.trim(),
            label: (o.label || o.value).trim() || o.value.trim(),
          }))
          .filter((o) => o.value.length > 0);
        if (opts.length === 0) {
          setError(
            `Field "${f.field_key}" uses type "${ft}" and needs at least one option (value + label).`,
          );
          return;
        }
        const vals = opts.map((o) => o.value);
        if (new Set(vals).size !== vals.length) {
          setError(`Field "${f.field_key}": each option value must be unique.`);
          return;
        }
      }
      const toSave: SubcategoryProductFieldItem[] = trimmed.map((f, i) => {
        const ft = (f.field_type || 'single_line_text').trim();
        const needsOpts = productFieldTypesRequiringOptions(ft);
        const field_options = needsOpts
          ? f.field_options
              .map((o) => ({
                value: o.value.trim(),
                label: (o.label || o.value).trim() || o.value.trim(),
              }))
              .filter((o) => o.value.length > 0)
          : undefined;
        return {
          field_key: f.field_key,
          display_order: i,
          default_display_label: (f.default_display_label || '').trim() || undefined,
          label_override: (f.label_override || '').trim() || undefined,
          is_visible: f.is_visible,
          field_type: ft,
          field_options,
        };
      });
      if (editingProductFieldsSubcategory) {
        await api.adminUpdateSubcategoryProductFields(editingProductFieldsSubcategory.id, toSave);
      } else if (editingProductFieldsCategory) {
        await api.adminUpdateCategoryProductFields(editingProductFieldsCategory.id, toSave);
      }
      setProductFieldsDialogOpen(false);
      setEditingProductFieldsSubcategory(null);
      setEditingProductFieldsCategory(null);
    } catch (err: any) {
      setError(err.message || 'Failed to save product fields');
    }
  };

  const handleOpenHardwareFieldsDialog = async (category: OrderCategory) => {
    setEditingHardwareFieldsCategory(category);
    setHardwareFieldsDialogOpen(true);
    try {
      const saved = await api.adminGetCategoryHardwareFields(category.id);
      const byKey = new Map(saved.map((f: any) => [f.field_key, f]));
      const merged = HARDWARE_FIELD_KEYS.map(({ key }, i) => {
        const existing = byKey.get(key);
        return existing
          ? { field_key: key, display_order: existing.display_order, label_override: existing.label_override ?? undefined, is_visible: existing.is_visible }
          : { field_key: key, display_order: 500 + i, label_override: undefined, is_visible: false };
      });
      setHardwareFieldsList(merged.sort((a, b) => a.display_order - b.display_order));
    } catch (err: any) {
      setError(err.message || 'Failed to load hardware fields');
      setHardwareFieldsList(
        HARDWARE_FIELD_KEYS.map(({ key }, i) => ({ field_key: key, display_order: i, label_override: undefined, is_visible: true }))
      );
    }
  };

  const handleSaveHardwareFields = async () => {
    if (!editingHardwareFieldsCategory) return;
    try {
      setError(null);
      const toSave = hardwareFieldsList
        .filter((f) => f.is_visible)
        .map((f, i) => ({ ...f, display_order: i }));
      await api.adminUpdateCategoryHardwareFields(editingHardwareFieldsCategory.id, toSave);
      setHardwareFieldsDialogOpen(false);
      setEditingHardwareFieldsCategory(null);
    } catch (err: any) {
      setError(err.message || 'Failed to save hardware fields');
    }
  };
  
  const categoryColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'slug', headerName: 'Slug', width: 200 },
    { field: 'display_order', headerName: 'Order', width: 100 },
    { field: 'is_active', headerName: 'Active', width: 100, type: 'boolean' },
    { field: 'actions', headerName: 'Actions', width: 150, sortable: false, renderCell: (params: any) => (
      <Box>
        <IconButton size="small" onClick={() => handleOpenCategoryDialog(params.row)}>
          <EditIcon />
        </IconButton>
        <IconButton size="small" onClick={() => handleDeleteCategory(params.row.id)} color="error">
          <DeleteIcon />
        </IconButton>
      </Box>
    )},
  ];
  
  const subcategoryColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'slug', headerName: 'Slug', width: 200 },
    { field: 'category_name', headerName: 'Category', width: 200, valueGetter: (params: any) => {
      const category = categories.find(c => c.id === params.row.category_id);
      return category?.name || 'N/A';
    }},
    { field: 'display_order', headerName: 'Order', width: 100 },
    { field: 'is_active', headerName: 'Active', width: 100, type: 'boolean' },
    { field: 'actions', headerName: 'Actions', width: 150, sortable: false, renderCell: (params: any) => (
      <Box>
        <IconButton size="small" onClick={() => handleOpenSubcategoryDialog(params.row)}>
          <EditIcon />
        </IconButton>
        <IconButton size="small" onClick={() => handleDeleteSubcategory(params.row.id)} color="error">
          <DeleteIcon />
        </IconButton>
      </Box>
    )},
  ];
  
  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Category Management</Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label="Categories" />
          <Tab label="Subcategories" />
          <Tab label="Product Fields" />
          <Tab label="Hardware Fields" />
          <Tab label="Upstream carriers" />
        </Tabs>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tabValue === 0 && (
              <Box>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenCategoryDialog()}
                  >
                    Add Category
                  </Button>
                </Box>
                <DataGrid
                  rows={categories}
                  columns={categoryColumns}
                  paginationModel={{ page: 0, pageSize: 25 }}
                  pageSizeOptions={[25, 50, 100]}
                  disableRowSelectionOnClick
                  autoHeight
                  getRowId={(row) => row.id}
                />
              </Box>
            )}
            
            {tabValue === 1 && (
              <Box>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenSubcategoryDialog()}
                  >
                    Add Subcategory
                  </Button>
                </Box>
                <DataGrid
                  rows={subcategories}
                  columns={subcategoryColumns}
                  paginationModel={{ page: 0, pageSize: 25 }}
                  pageSizeOptions={[25, 50, 100]}
                  disableRowSelectionOnClick
                  autoHeight
                  getRowId={(row) => row.id}
                />
              </Box>
            )}

            {tabValue === 2 && (
              <Box>
                <Tabs
                  value={productFieldsMainSubTab}
                  onChange={(_, v) => setProductFieldsMainSubTab(v)}
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                >
                  <Tab label="Category defaults" />
                  <Tab label="Subcategory overrides" />
                </Tabs>
                {productFieldsMainSubTab === 0 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Used for products that do not select a subcategory, and as fallback when a subcategory has no
                      field rows defined.
                    </Typography>
                    <DataGrid
                      rows={categories}
                      columns={[
                        { field: 'name', headerName: 'Category', width: 200 },
                        { field: 'slug', headerName: 'Slug', width: 140 },
                        {
                          field: 'actions',
                          headerName: 'Actions',
                          width: 180,
                          sortable: false,
                          renderCell: (params: any) => (
                            <Button
                              size="small"
                              startIcon={<SettingsIcon />}
                              onClick={() => handleOpenCategoryProductFieldsDialog(params.row)}
                            >
                              Category fields
                            </Button>
                          ),
                        },
                      ]}
                      paginationModel={{ page: 0, pageSize: 25 }}
                      pageSizeOptions={[25, 50, 100]}
                      disableRowSelectionOnClick
                      autoHeight
                      getRowId={(row) => row.id}
                    />
                  </Box>
                )}
                {productFieldsMainSubTab === 1 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      When a subcategory has at least one field row, those definitions replace the category defaults for
                      that subcategory.
                    </Typography>
                    <DataGrid
                      rows={subcategories}
                      columns={[
                        { field: 'name', headerName: 'Subcategory', width: 200 },
                        { field: 'slug', headerName: 'Slug', width: 140 },
                        {
                          field: 'category_name',
                          headerName: 'Category',
                          width: 180,
                          valueGetter: (params: any) => {
                            const cat = categories.find((c) => c.id === params.row.category_id);
                            return cat?.name ?? '—';
                          },
                        },
                        {
                          field: 'actions',
                          headerName: 'Actions',
                          width: 140,
                          sortable: false,
                          renderCell: (params: any) => (
                            <Button
                              size="small"
                              startIcon={<SettingsIcon />}
                              onClick={() => handleOpenProductFieldsDialog(params.row)}
                            >
                              Edit fields
                            </Button>
                          ),
                        },
                      ]}
                      paginationModel={{ page: 0, pageSize: 25 }}
                      pageSizeOptions={[25, 50, 100]}
                      disableRowSelectionOnClick
                      autoHeight
                      getRowId={(row) => row.id}
                    />
                  </Box>
                )}
              </Box>
            )}

            {tabValue === 3 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Define which hardware fields appear on equipment cards for each category (e.g. Internet). Only visible fields are shown on the order page.
                </Typography>
                <DataGrid
                  rows={categories}
                  columns={[
                    { field: 'name', headerName: 'Category', width: 200 },
                    { field: 'slug', headerName: 'Slug', width: 140 },
                    {
                      field: 'actions',
                      headerName: 'Actions',
                      width: 140,
                      sortable: false,
                      renderCell: (params: any) => (
                        <Button
                          size="small"
                          startIcon={<SettingsIcon />}
                          onClick={() => handleOpenHardwareFieldsDialog(params.row)}
                        >
                          Edit fields
                        </Button>
                      ),
                    },
                  ]}
                  paginationModel={{ page: 0, pageSize: 25 }}
                  pageSizeOptions={[25, 50, 100]}
                  disableRowSelectionOnClick
                  autoHeight
                  getRowId={(row) => row.id}
                />
              </Box>
            )}

            {tabValue === 4 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upstream carriers shown in the add/edit service form dropdown. Only active carriers appear in the dropdown.
                </Typography>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenUpstreamCarrierDialog()}
                  >
                    Add upstream carrier
                  </Button>
                </Box>
                <DataGrid
                  rows={upstreamCarriers}
                  columns={[
                    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
                    { field: 'display_order', headerName: 'Order', width: 90 },
                    {
                      field: 'is_active',
                      headerName: 'Active',
                      width: 90,
                      renderCell: (params: any) => (params.value ? <Chip label="Active" color="success" size="small" /> : <Chip label="Inactive" size="small" />),
                    },
                    {
                      field: 'actions',
                      headerName: 'Actions',
                      width: 140,
                      sortable: false,
                      renderCell: (params: any) => (
                        <>
                          <IconButton size="small" onClick={() => handleOpenUpstreamCarrierDialog(params.row)} title="Edit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteUpstreamCarrier(params.row)} title="Delete">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      ),
                    },
                  ]}
                  paginationModel={{ page: 0, pageSize: 25 }}
                  pageSizeOptions={[25, 50, 100]}
                  disableRowSelectionOnClick
                  autoHeight
                  getRowId={(row) => row.id}
                />
              </Box>
            )}
          </>
        )}
      </Paper>
      
      {/* Category Form Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Create Category'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Name"
                value={categoryFormData.name}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Slug"
                value={categoryFormData.slug}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, slug: e.target.value })}
                helperText="URL-friendly identifier"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={categoryFormData.description}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Logo URL"
                value={categoryFormData.logo_url}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, logo_url: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={categoryFormData.display_order}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, display_order: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={categoryFormData.is_active}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCategory} variant="contained" disabled={!categoryFormData.name || !categoryFormData.slug}>
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Subcategory Form Dialog */}
      <Dialog open={subcategoryDialogOpen} onClose={() => setSubcategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSubcategory ? 'Edit Subcategory' : 'Create Subcategory'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                select
                label="Category"
                value={subcategoryFormData.category_id}
                onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, category_id: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Name"
                value={subcategoryFormData.name}
                onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, name: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Slug"
                value={subcategoryFormData.slug}
                onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, slug: e.target.value })}
                helperText="URL-friendly identifier"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={subcategoryFormData.description}
                onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, description: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Logo URL"
                value={subcategoryFormData.logo_url}
                onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, logo_url: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={subcategoryFormData.display_order}
                onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, display_order: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={subcategoryFormData.is_active}
                    onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubcategoryDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSubcategory} variant="contained" disabled={!subcategoryFormData.name || !subcategoryFormData.slug || !subcategoryFormData.category_id}>
            {editingSubcategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Fields Dialog */}
      <Dialog
        open={productFieldsDialogOpen}
        onClose={() => {
          setProductFieldsDialogOpen(false);
          setEditingProductFieldsSubcategory(null);
          setEditingProductFieldsCategory(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Product fields —{' '}
          {editingProductFieldsSubcategory
            ? editingProductFieldsSubcategory.name
            : editingProductFieldsCategory
              ? `${editingProductFieldsCategory.name} (category defaults)`
              : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define which <strong>field keys</strong> appear on public plan/product cards. Values are edited per product
            in <strong>Product Management</strong>. Visible = shown on the public card. <strong>Field type</strong> sets
            how the value is edited (text, multiline, checkbox, dropdown, etc.). For dropdown and multiple checkboxes,
            add options below each row (stored value + display label). Keys must match <code>plan_features</code> keys
            on the product.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addProductFieldRow}>
              Add field
            </Button>
            <Button size="small" variant="outlined" onClick={loadInternetPlanFieldTemplate}>
              Load internet template
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 520, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 88 }}>Order</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 72 }} align="center">
                    Visible
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Field key</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Default label</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Label override</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Field type</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, width: 56 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedProductFields.map((row, index) => (
                  <React.Fragment key={row._rowId}>
                    <TableRow hover>
                      <TableCell sx={{ py: 0.5, verticalAlign: 'middle' }}>
                        <IconButton
                          size="small"
                          onClick={() => moveProductFieldRow(index, -1)}
                          disabled={index === 0}
                          aria-label="Move up"
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveProductFieldRow(index, 1)}
                          disabled={index === sortedProductFields.length - 1}
                          aria-label="Move down"
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5, verticalAlign: 'middle' }}>
                        <Checkbox
                          checked={row.is_visible}
                          onChange={(e) => updateProductFieldRow(row._rowId, { is_visible: e.target.checked })}
                          size="small"
                          inputProps={{ 'aria-label': 'Visible on plan card' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, verticalAlign: 'middle' }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.field_key}
                          onChange={(e) => updateProductFieldRow(row._rowId, { field_key: e.target.value })}
                          placeholder="e.g. monthly_price"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, verticalAlign: 'middle' }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.default_display_label ?? ''}
                          onChange={(e) => updateProductFieldRow(row._rowId, { default_display_label: e.target.value })}
                          placeholder="Default label"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, verticalAlign: 'middle' }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.label_override ?? ''}
                          onChange={(e) =>
                            updateProductFieldRow(row._rowId, { label_override: e.target.value || undefined })
                          }
                          placeholder="Optional override"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, verticalAlign: 'middle' }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel id={`pf-ft-${row._rowId}`}>Field type</InputLabel>
                          <Select
                            labelId={`pf-ft-${row._rowId}`}
                            label="Field type"
                            value={row.field_type || 'single_line_text'}
                            onChange={(e) => {
                              const field_type = String(e.target.value);
                              const needsOpts = productFieldTypesRequiringOptions(field_type);
                              const nextOpts =
                                needsOpts && row.field_options.length === 0
                                  ? [{ _oid: newProductFieldRowId(), value: '', label: '' }]
                                  : needsOpts
                                    ? row.field_options
                                    : [];
                              updateProductFieldRow(row._rowId, { field_type, field_options: nextOpts });
                            }}
                          >
                            {PRODUCT_FIELD_TYPE_OPTIONS.map((o) => (
                              <MenuItem key={o.value} value={o.value}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, verticalAlign: 'middle' }}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeProductFieldRow(row._rowId)}
                          aria-label="Remove field"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {productFieldTypesRequiringOptions(row.field_type || 'single_line_text') && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 1, borderTop: 0, bgcolor: 'action.hover' }}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            Options — value is stored on the product; label is shown in forms and the storefront.
                          </Typography>
                          {row.field_options.map((opt) => (
                            <Box
                              key={opt._oid}
                              sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}
                            >
                              <TextField
                                size="small"
                                label="Value"
                                value={opt.value}
                                onChange={(e) =>
                                  updateProductFieldOptionRow(row._rowId, opt._oid, { value: e.target.value })
                                }
                                sx={{ flex: '1 1 120px', minWidth: 120 }}
                              />
                              <TextField
                                size="small"
                                label="Label"
                                value={opt.label}
                                onChange={(e) =>
                                  updateProductFieldOptionRow(row._rowId, opt._oid, { label: e.target.value })
                                }
                                sx={{ flex: '1 1 140px', minWidth: 140 }}
                              />
                              <IconButton
                                size="small"
                                color="error"
                                aria-label="Remove option"
                                onClick={() => removeProductFieldOptionRow(row._rowId, opt._oid)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                          <Button size="small" variant="text" onClick={() => addProductFieldOption(row._rowId)}>
                            Add option
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {sortedProductFields.length === 0 && (
            <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
              No fields yet. Add a field or load the internet template.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setProductFieldsDialogOpen(false);
              setEditingProductFieldsSubcategory(null);
              setEditingProductFieldsCategory(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveProductFields} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hardware Fields Dialog */}
      <Dialog open={hardwareFieldsDialogOpen} onClose={() => setHardwareFieldsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Hardware fields — {editingHardwareFieldsCategory?.name ?? 'Category'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which fields appear on hardware/equipment cards for this category. Order determines display order.
          </Typography>
          <List dense disablePadding>
            {hardwareFieldsList
              .slice()
              .sort((a, b) => a.display_order - b.display_order)
              .map((item, index) => {
                const label = HARDWARE_FIELD_KEYS.find((f) => f.key === item.field_key)?.label ?? item.field_key;
                const setVisible = (is_visible: boolean) => {
                  setHardwareFieldsList((prev) =>
                    prev.map((f) => (f.field_key === item.field_key ? { ...f, is_visible } : f))
                  );
                };
                const setLabelOverride = (label_override: string) => {
                  setHardwareFieldsList((prev) =>
                    prev.map((f) => (f.field_key === item.field_key ? { ...f, label_override: label_override || undefined } : f))
                  );
                };
                const move = (delta: number) => {
                  const next = index + delta;
                  if (next < 0 || next >= hardwareFieldsList.length) return;
                  setHardwareFieldsList((prev) => {
                    const sorted = prev.slice().sort((a, b) => a.display_order - b.display_order);
                    const itemA = sorted[index];
                    const itemB = sorted[next];
                    return prev.map((f) => {
                      if (f.field_key === itemA.field_key) return { ...f, display_order: itemB.display_order };
                      if (f.field_key === itemB.field_key) return { ...f, display_order: itemA.display_order };
                      return f;
                    });
                  });
                };
                return (
                  <ListItem key={item.field_key} divider sx={{ alignItems: 'flex-start', gap: 1 }}>
                    <Checkbox
                      checked={item.is_visible}
                      onChange={(e) => setVisible(e.target.checked)}
                      size="small"
                      sx={{ pt: 0.5 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <ListItemText primary={label} secondary={item.field_key} primaryTypographyProps={{ variant: 'body2' }} />
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Label override (optional)"
                        value={item.label_override ?? ''}
                        onChange={(e) => setLabelOverride(e.target.value)}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <Box>
                      <IconButton size="small" onClick={() => move(-1)} disabled={index === 0} aria-label="Move up">
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => move(1)}
                        disabled={index === hardwareFieldsList.length - 1}
                        aria-label="Move down"
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItem>
                );
              })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHardwareFieldsDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveHardwareFields} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upstream Carrier Dialog */}
      <Dialog open={upstreamCarrierDialogOpen} onClose={() => setUpstreamCarrierDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUpstreamCarrier ? 'Edit upstream carrier' : 'Add upstream carrier'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Name"
                value={upstreamCarrierFormData.name}
                onChange={(e) => setUpstreamCarrierFormData({ ...upstreamCarrierFormData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display order"
                type="number"
                value={upstreamCarrierFormData.display_order}
                onChange={(e) => setUpstreamCarrierFormData({ ...upstreamCarrierFormData, display_order: parseInt(e.target.value, 10) || 0 })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={upstreamCarrierFormData.is_active}
                    onChange={(e) => setUpstreamCarrierFormData({ ...upstreamCarrierFormData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpstreamCarrierDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveUpstreamCarrier} variant="contained" disabled={!upstreamCarrierFormData.name.trim()}>
            {editingUpstreamCarrier ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CategoriesPage;

